import { EthAddressType, OauthLoginProvider } from 'multirpc-sdk';
import { createSelector } from 'reselect';

import { RootState } from 'store';
import {
  selectAuthAddress,
  selectAuthProvider,
  selectIsOAuth,
  selectIsWeb3Auth,
} from 'modules/auth/store/selectors';

import {
  selectAssociatedAccounts,
  selectAssociatedAccountsLoading,
  selectAssociatedAccountsState,
} from '../actions/fetchAssociatedAccounts';
import {
  selectUserETHAddresses,
  selectUserETHAddressesLoading,
  selectUserETHAddressesState,
} from '../actions/fetchUserETHAddresses';

const selectUserAccount = createSelector(
  selectAssociatedAccounts,
  selectAuthProvider,
  selectIsWeb3Auth,
  (accounts, authProvider, isWeb3Auth) => {
    if (!authProvider) {
      return undefined;
    }

    const provider = isWeb3Auth
      ? OauthLoginProvider.Email
      : authProvider.toString();

    return accounts.find(account => account.provider === provider);
  },
);

export const selectUserEmail = createSelector(
  selectUserAccount,
  account => account?.email,
);

export const selectUserNickname = createSelector(
  selectUserAccount,
  account => account?.login,
);

const selectUserETHAddress = createSelector(
  (state: RootState) => selectUserETHAddresses(state),
  addresses => {
    const createdByUserAddress = addresses.find(
      address => address.type === EthAddressType.User,
    );

    if (createdByUserAddress) {
      return createdByUserAddress;
    }

    const generatedAddress = addresses.find(
      address => address.type === EthAddressType.Generated,
    );

    return generatedAddress;
  },
);

export const selectUserAddress = createSelector(
  selectUserETHAddress,
  selectAuthAddress,
  selectIsWeb3Auth,
  (address, authAddress, isWeb3Auth) => {
    if (isWeb3Auth) {
      return authAddress;
    }

    return address?.address;
  },
);

export const selectUserAddressType = createSelector(
  selectUserETHAddress,
  selectIsWeb3Auth,
  (address, isWeb3Auth) => {
    if (isWeb3Auth) {
      return EthAddressType.User;
    }

    return address?.type;
  },
);

export const selectHasWeb3Account = createSelector(
  selectUserAddressType,
  addressType => addressType === EthAddressType.User,
);

// A user who connected his google account to his web3 account and logged in
// using google
export const selectIsWeb3OAuth = createSelector(
  selectHasWeb3Account,
  selectIsOAuth,
  (hasWeb3Account, isOAuth) => isOAuth && hasWeb3Account,
);

export const selectIsUserInitializing = createSelector(
  selectAssociatedAccountsLoading,
  selectUserETHAddressesLoading,
  (associatedAccountsLoading, userETHAddressesLoading) =>
    associatedAccountsLoading || userETHAddressesLoading,
);

export const selectAssociatedAccountsLoaded = createSelector(
  selectAssociatedAccountsState,
  state => state.isSuccess,
);

export const selectUserETHAddressesLoaded = createSelector(
  selectUserETHAddressesState,
  state => state.isSuccess,
);

export const selectIsUserInitialized = createSelector(
  selectAssociatedAccountsLoaded,
  selectUserETHAddressesLoaded,
  selectIsWeb3Auth,
  (associatedAccountsLoaded, userETHAddressesLoaded, isWeb3Auth) =>
    isWeb3Auth || (associatedAccountsLoaded && userETHAddressesLoaded),
);

export const selectWeb3UserAccount = createSelector(
  selectAssociatedAccounts,
  accounts => accounts.find(account => Boolean(account.address)),
);

export const selectGoogleUserAccount = createSelector(
  selectAssociatedAccounts,
  accounts =>
    accounts.find(({ provider }) => provider === OauthLoginProvider.Google),
);

export const selectGithubUserAccount = createSelector(
  selectAssociatedAccounts,
  accounts =>
    accounts.find(({ provider }) => provider === OauthLoginProvider.Github),
);
